import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  AlertTriangle,
  Camera01,
  Check,
  Loading01 as Loader,
  RefreshCw01,
  Trash01,
  X,
} from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { VERDICT_COPY } from "./photo-verdict"
import type { QEPhoto } from "./types"

// Quick Estimate 3 — quality editor.
//
// Surface for the photos step. Mirrors PhotoEditor's layout so the
// interaction grammar stays the same across steps, but keeps the
// scope tight: this is purely about "is this shot usable?". The
// side picker lives on the next step (sides editor).
//
// Branches:
//   • good      → small green confirmation, optional note, Remove + Done
//   • low       → amber verdict box, Retake / Keep anyway / Remove
//   • failed    → rose verdict box, Retry / Use without tag / Remove

interface QualityEditorProps {
  open: boolean
  photo: QEPhoto | null
  canRetry: boolean
  retrying: boolean
  onClose: () => void
  onChangeDescription: (text: string) => void
  onRetake?: () => void
  onRetry?: () => void
  onKeepAnyway?: () => void
  onSendWithoutTag?: () => void
  onRemove: () => void
}

export function QualityEditor({
  open,
  photo,
  canRetry,
  retrying,
  onClose,
  onChangeDescription,
  onRetake,
  onRetry,
  onKeepAnyway,
  onSendWithoutTag,
  onRemove,
}: QualityEditorProps) {
  const [description, setDescription] = useState(photo?.description ?? "")

  useEffect(() => {
    if (open && photo) setDescription(photo.description ?? "")
  }, [open, photo])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!photo) return null

  const failed = photo.verdict === "eval-failed"
  const kept = !!photo.sendWithoutAiTag
  const lowQuality =
    !failed && !kept && photo.uploadedUrl.length > 0 && !photo.qualified
  const verdictCopy = VERDICT_COPY[photo.verdict]

  const tone: "good" | "low" | "failed" | "kept" = failed
    ? "failed"
    : kept
      ? "kept"
      : lowQuality
        ? "low"
        : "good"

  const headerSubtitle =
    tone === "good"
      ? "Looks clean to the AI."
      : tone === "kept"
        ? "Sending despite a quality flag."
        : tone === "failed"
          ? "AI couldn't review this shot."
          : verdictCopy.title

  const flushDescription = () => {
    if (description !== (photo.description ?? "")) {
      onChangeDescription(description)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute inset-0 bg-zinc-950/60 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className={cx(
              "relative z-10 w-full sm:max-w-md max-h-[92vh] overflow-y-auto",
              "bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-zinc-900/30",
            )}
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
          >
            <div className="sm:hidden pt-2 pb-1 flex justify-center">
              <span className="block w-10 h-1.5 rounded-full bg-zinc-200" />
            </div>

            <div className="flex items-center justify-between gap-3 px-5 sm:px-6 pt-3 sm:pt-5 pb-3">
              <div className="min-w-0">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400">
                  Photo quality
                </p>
                <h3 className="text-[16px] font-bold text-zinc-900 leading-tight truncate">
                  {headerSubtitle}
                </h3>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="size-9 rounded-xl border border-zinc-200 hover:bg-zinc-50 grid place-items-center text-zinc-500 transition-colors duration-150 shrink-0"
              >
                <X className="size-4" aria-hidden />
              </button>
            </div>

            <div className="px-5 sm:px-6">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-zinc-200/70 bg-zinc-100">
                <img
                  src={photo.previewUrl}
                  alt="Captured photo"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="px-5 sm:px-6 py-5 space-y-5">
              {tone === "failed" && (
                <VerdictBlock
                  tone="rose"
                  title="Couldn't review this photo"
                  body={verdictCopy.body}
                />
              )}
              {tone === "low" && (
                <VerdictBlock
                  tone="amber"
                  title={verdictCopy.title}
                  body={verdictCopy.body}
                />
              )}
              {tone === "good" && (
                <VerdictBlock
                  tone="emerald"
                  title="AI thinks this is clean"
                  body="Clear framing, well lit, damage in view."
                />
              )}
              {tone === "kept" && (
                <VerdictBlock
                  tone="zinc"
                  title="Sending without an AI tag"
                  body="The AI couldn't tag this shot. You'll set the side on the next step."
                />
              )}

              {/* Optional note — same field across tones. */}
              <div>
                <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5 block">
                  Note for the shop (optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={flushDescription}
                  placeholder="Anything the shop should know about this shot."
                  rows={3}
                  maxLength={400}
                  className="w-full rounded-2xl bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-[13.5px] text-zinc-800 placeholder:text-zinc-400 leading-relaxed resize-none outline-none focus:bg-white focus:border-zinc-400 transition-colors duration-150"
                />
              </div>
            </div>

            {/* Action row — Done sits on its own line on mobile (full
                width, primary), secondaries flow above. Inlines on
                desktop. */}
            <div className="border-t border-zinc-100 bg-white px-5 sm:px-6 py-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="flex flex-wrap gap-2 sm:contents">
                <button
                  type="button"
                  onClick={onRemove}
                  className="h-11 px-4 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                >
                  <Trash01 className="size-4" aria-hidden />
                  Remove
                </button>

                {tone === "low" && onRetake && (
                  <button
                    type="button"
                    onClick={onRetake}
                    className="h-11 px-4 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-800 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                  >
                    <Camera01 className="size-4" aria-hidden />
                    Retake
                  </button>
                )}
                {tone === "low" && onKeepAnyway && (
                  <button
                    type="button"
                    onClick={onKeepAnyway}
                    className="h-11 px-4 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-800 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                  >
                    <Check className="size-4" aria-hidden />
                    Keep anyway
                  </button>
                )}

                {tone === "failed" && canRetry && onRetry && (
                  <button
                    type="button"
                    disabled={retrying}
                    onClick={onRetry}
                    className="h-11 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                  >
                    {retrying ? (
                      <Loader className="size-4 animate-spin" aria-hidden />
                    ) : (
                      <RefreshCw01 className="size-4" aria-hidden />
                    )}
                    Retry review
                  </button>
                )}
                {tone === "failed" && onSendWithoutTag && (
                  <button
                    type="button"
                    onClick={onSendWithoutTag}
                    className="h-11 px-4 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-800 text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                  >
                    <Check className="size-4" aria-hidden />
                    Use without tag
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  flushDescription()
                  onClose()
                }}
                className="h-11 w-full sm:w-auto sm:ml-auto px-5 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
              >
                <Check className="size-4" aria-hidden />
                Done
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/* ── Verdict block ────────────────────────────────────────────── */

function VerdictBlock({
  tone,
  title,
  body,
}: {
  tone: "rose" | "amber" | "emerald" | "zinc"
  title: string
  body: string
}) {
  const palette = {
    rose: {
      box: "bg-rose-50 border-rose-200",
      iconBg: "bg-rose-100",
      iconText: "text-rose-700",
      title: "text-rose-900",
      body: "text-rose-800/85",
    },
    amber: {
      box: "bg-amber-50 border-amber-200",
      iconBg: "bg-amber-100",
      iconText: "text-amber-700",
      title: "text-amber-900",
      body: "text-amber-800/85",
    },
    emerald: {
      box: "bg-emerald-50 border-emerald-200",
      iconBg: "bg-emerald-100",
      iconText: "text-emerald-700",
      title: "text-emerald-900",
      body: "text-emerald-800/85",
    },
    zinc: {
      box: "bg-zinc-50 border-zinc-200",
      iconBg: "bg-zinc-100",
      iconText: "text-zinc-600",
      title: "text-zinc-900",
      body: "text-zinc-600",
    },
  }[tone]

  const Icon = tone === "emerald" ? Check : AlertTriangle

  return (
    <div className={cx("rounded-2xl border p-3.5 flex items-start gap-3", palette.box)}>
      <span
        className={cx(
          "size-7 rounded-lg grid place-items-center shrink-0 mt-0.5",
          palette.iconBg,
        )}
      >
        <Icon
          className={cx("size-3.5", palette.iconText)}
          strokeWidth={tone === "emerald" ? 3 : 2}
          aria-hidden
        />
      </span>
      <div className="min-w-0">
        <p className={cx("text-[13px] font-bold leading-tight", palette.title)}>
          {title}
        </p>
        <p className={cx("text-[12.5px] leading-snug mt-0.5", palette.body)}>
          {body}
        </p>
      </div>
    </div>
  )
}
